import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Bot, CheckCircle2, AlertCircle, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AIAgentStatus = ({ brandId, refreshTrigger }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (brandId) {
      fetchStatus();
    }
  }, [brandId, refreshTrigger]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/brands/${brandId}/setup-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching AI agent status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setActivating(true);
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/brands/${brandId}/activate-ai-agent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('🤖 AI Agent activated successfully!');
        fetchStatus();
      } else {
        toast.error(response.data.message || 'Failed to activate AI Agent');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate AI Agent');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-gray-200">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status) return null;

  const { complete, ai_agent_active, steps_completed, total_steps, steps_remaining } = status;

  return (
    <Card className={`border-2 ${
      ai_agent_active 
        ? 'border-green-500 bg-green-50' 
        : complete 
        ? 'border-blue-500 bg-blue-50' 
        : 'border-orange-500 bg-orange-50'
    }`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${
              ai_agent_active 
                ? 'bg-green-100' 
                : complete 
                ? 'bg-blue-100' 
                : 'bg-orange-100'
            }`}>
              <Bot className={`w-6 h-6 ${
                ai_agent_active 
                  ? 'text-green-600' 
                  : complete 
                  ? 'text-blue-600' 
                  : 'text-orange-600'
              }`} />
            </div>
            <div>
              <CardTitle className="text-xl">AI Agent Status</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                {ai_agent_active ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-green-700 font-medium">Active & Running</span>
                  </>
                ) : complete ? (
                  <>
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span className="text-blue-700 font-medium">Ready to Activate</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <span className="text-orange-700 font-medium">Setup Incomplete</span>
                  </>
                )}
              </CardDescription>
            </div>
          </div>
          {complete && !ai_agent_active && (
            <Button 
              onClick={handleActivate}
              disabled={activating}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {activating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Activate AI Agent
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {ai_agent_active ? (
          <div className="space-y-3">
            <div className="p-4 bg-white rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                🎉 <strong>Your AI Agent is actively working!</strong> It's generating content, optimizing SEO, and managing your social media presence automatically.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 pt-2">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">✓</p>
                <p className="text-xs text-gray-600 mt-1">Content Generation</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">✓</p>
                <p className="text-xs text-gray-600 mt-1">SEO Optimization</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">✓</p>
                <p className="text-xs text-gray-600 mt-1">Social Posting</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">Setup Progress</span>
                <span className="font-bold text-gray-900">{steps_completed}/{total_steps} Steps</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-500 ${
                    complete ? 'bg-green-500' : 'bg-orange-500'
                  }`}
                  style={{ width: `${(steps_completed / total_steps) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Steps Remaining */}
            {steps_remaining && steps_remaining.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">Complete these steps to activate AI Agent:</p>
                {steps_remaining.map((step, index) => (
                  <div key={step.step} className="p-3 bg-white rounded-lg border border-orange-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                          <h4 className="font-semibold text-gray-900">{step.title}</h4>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 ml-8">{step.description}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.location.href = step.action}
                      >
                        Complete →
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {complete && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  ✨ <strong>Setup Complete!</strong> Click "Activate AI Agent" to start automation.
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AIAgentStatus;
